import type { QueryKey } from '@tanstack/react-query';

import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetAllComponentsResponse, GetBinaryComponentPathsParams } from '../../../../shared/types/routes/binary';

/**
 * Function to fetch binary component paths.
 * Note that we don't use this hook like other React Query hooks,
 * we use it with `ensureQueryData` which requires that we provide it a fetch function directly.
 * @param {string} locator - User project locator.
 * @param {GetBinaryComponentPathsParams} params - The request query params
 */
export function getBinaryComponentPaths(
  locator: string,
  params: GetBinaryComponentPathsParams,
) {
  return get<GetAllComponentsResponse>(`/api/binary/revision/${nuc(locator)}/components/paths`, false, {
    ...params,
    path: params.path ? nuc(params.path) : undefined,
  });
}

export const getBinaryComponentPathsQueryKey = (locator: string, params: GetBinaryComponentPathsParams): QueryKey => ['revisions', locator, 'components', 'paths', params];

/**
 * useGetBinaryComponentPaths is a hook that fetches and returns the binary component paths for the given locator.
 */
export default function useGetBinaryComponentPaths(locator: string, params: GetBinaryComponentPathsParams) {
  return useQuery<GetAllComponentsResponse>({
    queryKey: getBinaryComponentPathsQueryKey(locator, params),
    queryFn: () => getBinaryComponentPaths(locator, params),
  });
}
